import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Typography, Box, Stack } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getCustomHeatMapStats } from "../../../../store/features/getHeatMapStats";
import ChartTooltip from "../../Home/ChartTooltip";

const hours = [
  "12am","1am","2am","3am","4am","5am","6am","7am","8am","9am","10am","11am",
  "12pm","1pm","2pm","3pm","4pm","5pm","6pm","7pm","8pm","9pm","10pm","11pm"
];
const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const monthLabels = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const displayedDayLabels = [1, 10, 15, 25, 30];

const COLORS = { high: "#148DFD", medium: "#A0D4FF", low: "#D6EDFF", veryLow: "#F7F8F9" };
const getIntensity = (v) => (v === 0 ? "veryLow" : v <= 2 ? "low" : v <= 5 ? "medium" : "high");
const monthLabelToIndex = (label) => monthLabels.findIndex((m) => m === label);

const parseISO = (s) => new Date(s);
const diffDaysInclusive = (a, b) => {
  const start = parseISO(a); const end = parseISO(b);
  const ms = end.setHours(0,0,0,0) - start.setHours(0,0,0,0);
  return Math.floor(ms / (1000*60*60*24)) + 1;
};
const listMonthsBetween = (a, b) => {
  const start = parseISO(a); const end = parseISO(b);
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const months = [];
  while (cur <= end) {
    months.push({ y: cur.getFullYear(), m: cur.getMonth() });
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
};
const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

export default function CustomHeatMap({ groupId }) {
  const dispatch = useDispatch();
  const [tooltip, setTooltip] = useState({ visible: false, position: null, data: null });

  const { startDate, endDate } = useSelector((s) => s.analyticsOverviewStats.dateRange);
  const { data: customDataState } = useSelector((s) => s.heatMapStats.customData);
  const apiRows = customDataState?.data || [];

  useEffect(() => {
    if (!groupId || !startDate || !endDate) return;
    dispatch(getCustomHeatMapStats({ startDate, endDate, group_id: groupId }));
  }, [dispatch, startDate, endDate, groupId]);

  const totalDays = useMemo(() => (startDate && endDate ? diffDaysInclusive(startDate, endDate) : 0), [startDate, endDate]);

  const normalized = useMemo(() => {
    return apiRows.map((r) => {
      const mi = r.monthLabel != null ? monthLabelToIndex(r.monthLabel) : null;
      return {
        year: r.year,
        monthIndex: mi,
        monthLabel: r.monthLabel,
        day: r.day ?? null,
        dayName: r.dayName ?? null,
        hour: typeof r.hour === "number" ? r.hour : null,
        totalChats: r.totalChats || 0,
        totalTickets: r.totalTickets || 0,
        totalActivity: r.totalActivity || 0,
      };
    });
  }, [apiRows]);

  // Helpers to read cell
  const showTooltip = (event, title, stats) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({ visible: true, position: { x: rect.left + rect.width / 2, y: rect.top }, data: { title, stats } });
  };
  const hideTooltip = () => setTooltip({ visible: false, position: null, data: null });

  // Weekly mode (<=7 days): 7 rows (Mon..Sun) x 24 hours
  const WeeklyGrid = () => {
    const byDayHour = {};
    const byDayHourStats = {};
    const daysPresent = new Set();
    days.forEach((d) => {
      byDayHour[d] = Array(24).fill(0);
      byDayHourStats[d] = Array(24).fill(null).map(() => ({ chats: 0, tickets: 0 }));
    });
    normalized.forEach((r) => {
      // Derive dayName if missing using year/month/day
      let dname = r.dayName;
      if (!dname && r.year != null && r.monthIndex != null && r.day != null) {
        const dt = new Date(r.year, r.monthIndex, r.day);
        dname = days[dt.getDay() === 0 ? 6 : dt.getDay() - 1];
      }
      const h = r.hour != null ? r.hour : 12; // fallback to noon if hour missing
      if (dname && byDayHour[dname]) {
        daysPresent.add(dname);
        byDayHour[dname][h] += r.totalActivity;
        byDayHourStats[dname][h].chats += r.totalChats || 0;
        byDayHourStats[dname][h].tickets += r.totalTickets || 0;
      }
    });
    return (
      <>
        {days.map((d, di) => (
          <Stack key={d} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ width: "50px", flexShrink: 0 }}>
              <Typography sx={{ color: "#405261", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 400, lineHeight: "16px",float: 'right' }}>{daysPresent.has(d) ? d : '-'}</Typography>
            </Box>
            {byDayHour[d].map((v, hi) => (
              <Box
                key={`${d}-${hi}`}
                onMouseEnter={(e) => showTooltip(e, `Activity`, [
                  { label: "Total Chats", value: byDayHourStats[d][hi].chats },
                  { label: "Total Tickets", value: byDayHourStats[d][hi].tickets },
                ])}
                onMouseLeave={hideTooltip}
                sx={{ minWidth: { xs: "37px", md: "37px" }, height: { xs: "33px", md: "33px" }, flex: 1, borderRadius: "8px", bgcolor: COLORS[getIntensity(v)], transition: "all 0.2s ease", cursor: "pointer", '&:hover': { transform: 'scale(1.05)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' } }}
              />
            ))}
          </Stack>
        ))}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, pl: "60px" }}>
          {hours.map((h) => (
            <Box key={h} sx={{ minWidth: { xs: "37px", md: "37px" }, flex: 1, textAlign: "center" }}>
              <Typography sx={{ color: "#405261", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 400, lineHeight: "16px" }}>{h}</Typography>
            </Box>
          ))}
        </Stack>
      </>
    );
  };

  // Monthly-like mode (<20 days): rows are days across each month in range, columns are 24 hours
  const MonthlyLikeGrid = () => {
    const listDatesInclusive = (a, b) => {
      const start = parseISO(a);
      const end = parseISO(b);
      const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const out = [];
      while (cur <= end) {
        out.push({ y: cur.getFullYear(), m: cur.getMonth(), d: cur.getDate() });
        cur.setDate(cur.getDate() + 1);
      }
      return out;
    };

    const daysRange = listDatesInclusive(startDate, endDate);

    const byDateHour = new Map();
    const byDateHourStats = new Map();
    daysRange.forEach(({ y, m, d }) => {
      const key = `${y}-${m}-${d}`;
      byDateHour.set(key, Array(24).fill(0));
      byDateHourStats.set(key, Array(24).fill(null).map(() => ({ chats: 0, tickets: 0 })));
    });

    normalized.forEach((r) => {
      if (r.year != null && r.monthIndex != null && r.day != null) {
        const key = `${r.year}-${r.monthIndex}-${r.day}`;
        if (byDateHour.has(key)) {
          const h = r.hour != null ? r.hour : 12;
          const row = byDateHour.get(key);
          row[h] += r.totalActivity;
          const srow = byDateHourStats.get(key);
          srow[h].chats += r.totalChats || 0;
          srow[h].tickets += r.totalTickets || 0;
        }
      }
    });

    return (
      <>
        {daysRange.map(({ y, m, d }, idx) => {
          const key = `${y}-${m}-${d}`;
          const row = byDateHour.get(key) || Array(24).fill(0);
          const srow = byDateHourStats.get(key) || Array(24).fill({ chats: 0, tickets: 0 });
          const showDayLabel = displayedDayLabels.includes(idx + 1) || d === 1;
          return (
            <Stack key={key} direction="row" spacing={1} alignItems="center" sx={{ mb: "2px" }}>
              <Box sx={{ width: "50px", flexShrink: 0 }}>
                {showDayLabel && (
                  <Typography sx={{ color: "#405261", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 400, lineHeight: "10px" }}>
                    {`${monthLabels[m]} ${d}`}
                  </Typography>
                )}
              </Box>
              {row.map((v, hi) => (
                <Box
                  key={`${key}-${hi}`}
                  onMouseEnter={(e) => showTooltip(e, `Activity`, [
                    { label: "Total Chats", value: srow[hi].chats },
                    { label: "Total Tickets", value: srow[hi].tickets },
                  ])}
                  onMouseLeave={hideTooltip}
                  sx={{ minWidth: { xs: "37px", md: "37px" }, height: { xs: "8px", md: "8px" }, flex: 1, borderRadius: "8px", bgcolor: COLORS[getIntensity(v)], transition: "all 0.2s ease", cursor: "pointer", '&:hover': { transform: 'scale(1.05)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' } }}
                />
              ))}
            </Stack>
          );
        })}
        <Stack direction="row" spacing={1} sx={{ mb: 1, pl: "60px" }}>
          {hours.map((h) => (
            <Box key={h} sx={{ minWidth: { xs: "37px", md: "37px" }, flex: 1, textAlign: "center" }}>
              <Typography sx={{ color: "#405261", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 400, lineHeight: "16px" }}>
                {h}
              </Typography>
            </Box>
          ))}
        </Stack>
      </>
    );
  };

  // Yearly-like mode (>30 days): rows are months Jan..Dec, columns are 24 hours
  const YearlyLikeGrid = () => {
    const byMonthHour = monthLabels.map(() => Array(24).fill(0));
    const byMonthHourStats = monthLabels.map(() => Array(24).fill(null).map(() => ({ chats: 0, tickets: 0 })));
    const monthsInRange = new Set(listMonthsBetween(startDate, endDate).map(({ m }) => m));
    normalized.forEach((r) => {
      const mi = r.monthIndex;
      if (mi != null) {
        const h = r.hour != null ? r.hour : 12;
        byMonthHour[mi][h] += r.totalActivity;
        byMonthHourStats[mi][h].chats += r.totalChats || 0;
        byMonthHourStats[mi][h].tickets += r.totalTickets || 0;
      }
    });
    return (
      <>
        {monthLabels.map((ml, mi) => (
          <Stack key={ml} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ width: "50px", flexShrink: 0 }}>
              <Typography sx={{ color: "#405261", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 400, lineHeight: "16px",float: 'right' }}>{monthsInRange.has(mi) ? ml : '-'}</Typography>
            </Box>
            {byMonthHour[mi].map((v, hi) => (
              <Box
                key={`${ml}-${hi}`}
                onMouseEnter={(e) => showTooltip(e, `Activity`, [
                  { label: "Total Chats", value: byMonthHourStats[mi][hi].chats },
                  { label: "Total Tickets", value: byMonthHourStats[mi][hi].tickets },
                ])}
                onMouseLeave={hideTooltip}
                sx={{ minWidth: { xs: "35px", md: "35px" }, height: { xs: "19px", md: "19px" }, flex: 1, borderRadius: "8px", bgcolor: COLORS[getIntensity(v)], transition: "all 0.2s ease", cursor: "pointer", '&:hover': { transform: 'scale(1.05)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' } }}
              />
            ))}
          </Stack>
        ))}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, pl: "60px" }}>
          {hours.map((h) => (
            <Box key={h} sx={{ minWidth: { xs: "35px", md: "35px" }, flex: 1, textAlign: "center" }}>
              <Typography sx={{ color: "#405261", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 400, lineHeight: "16px" }}>{h}</Typography>
            </Box>
          ))}
        </Stack>
      </>
    );
  };

  const Title = () => {
    let subtitle = "";
    if (totalDays <= 7) subtitle = "Weekly view (Mon-Sun)";
    else if (totalDays <= 30) subtitle = "Monthly view (days across months)";
    else subtitle = "Yearly view (months)";
    return (
      <Box>
        <Typography sx={{ color: "#061522", fontFamily: "Inter, sans-serif", fontSize: "18px", fontWeight: 500, lineHeight: "24px", mb: 0.5 }}>
          Chat/Ticket Heatmap
        </Typography>
        <Typography sx={{ color: "#405261", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 400, lineHeight: "20px" }}>
          {subtitle}
        </Typography>
      </Box>
    );
  };

  return (
    <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #E5E7EB", width: "100%" }}>
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Title />
        </Stack>

        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: 600 }}>
            {totalDays <= 7 ? <WeeklyGrid /> : totalDays <= 30 ? <MonthlyLikeGrid /> : <YearlyLikeGrid />}
          </Box>
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ mt: 3, flexWrap: "wrap" }}>
          <Typography sx={{ color: "#405261", fontFamily: "Inter, sans-serif", fontSize: "14px", fontWeight: 400, lineHeight: "16px" }}>Low</Typography>
          <Box sx={{ width: "48px", height: "8px", borderRadius: "4px", bgcolor: COLORS.veryLow }} />
          <Box sx={{ width: "48px", height: "8px", borderRadius: "4px", bgcolor: COLORS.low }} />
          <Box sx={{ width: "48px", height: "8px", borderRadius: "4px", bgcolor: COLORS.medium }} />
          <Box sx={{ width: "48px", height: "8px", borderRadius: "4px", bgcolor: COLORS.high }} />
          <Typography sx={{ color: "#405261", fontFamily: "Inter, sans-serif", fontSize: "14px", fontWeight: 400, lineHeight: "16px" }}>High</Typography>
        </Stack>
      </CardContent>

      {tooltip.visible && tooltip.data && (
        <ChartTooltip title={tooltip.data.title} stats={tooltip.data.stats} position={tooltip.position} />
      )}
    </Card>
  );
}
